import { auth } from '../auth.js';
import { api } from '../api.js';
import { escapeHtml } from '../utils.js';
import { addDays, dateKey, startOfDay, upcomingEntries } from '../calendar-model.js';
import { entryTime } from '../calendar-view.js';
import { getEvents, isLive, eventHref } from '../events-nav.js';

/**
 * Landing page for a signed-in user: what is happening next, then the tiles.
 *
 * The two panels answer the question someone actually opens the portal with -- is anything
 * on today? -- so they sit above the tiles rather than beside them. Each one fills itself
 * after the page is painted: the hero and the tiles need no network, and making the whole
 * page wait on two requests would leave it blank for as long as the slower one takes.
 */

/** How far ahead each panel looks, and how many rows it will show before deferring to its page. */
const CALENDAR_DAYS = 14;
const CALENDAR_LIMIT = 5;
const EVENT_LIMIT = 4;

/**
 * Bumped on every render, so a slow answer for a page the user has already left cannot write
 * into the DOM that replaced it. The element check alone is not enough -- navigating back to
 * Home re-creates an element with the same id.
 */
let renderId = 0;

export function renderHome() {
    const myRender = ++renderId;
    const user = auth.getUser();
    const firstName = (user?.full_name ?? '').trim().split(/\s+/)[0] || 'there';

    document.getElementById('app').innerHTML = `
        <div class="container home-container">
            <section class="hero-section">
                <h1>Hello, ${escapeHtml(firstName)}</h1>
                <p class="hero-subtitle">Welcome to the family portal.</p>
            </section>

            <div class="home-panels">
                <section class="home-panel" aria-labelledby="home-calendar-heading">
                    <div class="home-panel-header">
                        <h2 id="home-calendar-heading"><i class="fa-solid fa-calendar-week"></i> Coming up</h2>
                        <a href="/calendar" data-link class="home-panel-all">Calendar</a>
                    </div>
                    <div class="home-panel-body" id="home-calendar-body">
                        <p class="home-panel-loading">Loading the calendar…</p>
                    </div>
                </section>

                <section class="home-panel" aria-labelledby="home-events-heading">
                    <div class="home-panel-header">
                        <h2 id="home-events-heading"><i class="fa-solid fa-calendar-days"></i> Events</h2>
                        <a href="/events" data-link class="home-panel-all">All events</a>
                    </div>
                    <div class="home-panel-body" id="home-events-body">
                        <p class="home-panel-loading">Loading events…</p>
                    </div>
                </section>
            </div>

            <div class="features-grid">
                <a href="/gallery" data-link class="feature-card">
                    <div class="feature-icon"><i class="fa-solid fa-images"></i></div>
                    <h3>Gallery</h3>
                    <p>${auth.isLimited() ? 'Upload and keep your photos.' : 'Upload photos and share them with the family.'}</p>
                </a>
                ${auth.isLimited() ? '' : `
                <a href="/shared" data-link class="feature-card">
                    <div class="feature-icon"><i class="fa-solid fa-share-nodes"></i></div>
                    <h3>Shared with me</h3>
                    <p>Photos other people have shared with you.</p>
                </a>`}
                <a href="/profile" data-link class="feature-card">
                    <div class="feature-icon"><i class="fa-solid fa-user-gear"></i></div>
                    <h3>Your account</h3>
                    <p>Change your name, password, or set up two-factor sign-in.</p>
                </a>
                ${auth.isAdmin() ? `
                <a href="/admin" data-link class="feature-card">
                    <div class="feature-icon"><i class="fa-solid fa-shield-halved"></i></div>
                    <h3>Admin</h3>
                    <p>Manage family accounts.</p>
                </a>` : ''}
            </div>
        </div>`;

    void fillCalendarPanel(myRender);
    void fillEventsPanel(myRender);
}

/** Writes into a panel only if the page it belongs to is still the one on screen. */
function paint(id, myRender, html) {
    if (myRender !== renderId) return;
    const body = document.getElementById(id);
    if (body) body.innerHTML = html;
}

/** "Today", "Tomorrow", or "Sat 11 Oct". */
function dayLabel(day) {
    const today = startOfDay(new Date());
    if (dateKey(day) === dateKey(today)) return 'Today';
    if (dateKey(day) === dateKey(addDays(today, 1))) return 'Tomorrow';
    return day.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * The empty state is a link, not a sentence: on a page whose whole job is "what's next", the
 * answer "nothing" should also be the way to add something.
 */
function emptyPanel(href, icon, text, action) {
    return `
        <a href="${href}" data-link class="home-panel-empty">
            <i class="fa-solid ${icon}"></i>
            <span class="home-panel-empty-text">${escapeHtml(text)}</span>
            <span class="home-panel-empty-action">${escapeHtml(action)} <i class="fa-solid fa-arrow-right"></i></span>
        </a>`;
}

function errorPanel(text) {
    return `<p class="home-panel-error"><i class="fa-solid fa-triangle-exclamation"></i> ${escapeHtml(text)}</p>`;
}

async function fillCalendarPanel(myRender) {
    const from = startOfDay(new Date());
    const to = addDays(from, CALENDAR_DAYS);

    let events;
    try {
        ({ events } = await api.listCalendarEvents(from, to));
    } catch {
        // A panel that cannot load must not break the page it decorates.
        paint('home-calendar-body', myRender, errorPanel("Couldn't load the calendar."));
        return;
    }

    // `new Date()` again rather than `from`: an event that ended in the last few seconds is
    // not coming up, and the fetch may have taken a moment.
    const coming = upcomingEntries(events, new Date(), CALENDAR_DAYS, CALENDAR_LIMIT);
    if (coming.length === 0) {
        paint('home-calendar-body', myRender, emptyPanel(
            '/calendar?new=1', 'fa-calendar-plus',
            'Nothing in the next two weeks.', 'Add something',
        ));
        return;
    }

    const rows = coming.map(({ day, entry }) => `
        <a href="/calendar" data-link class="home-panel-row">
            <span class="home-panel-when">
                <span class="home-panel-day">${escapeHtml(dayLabel(day))}</span>
                <span class="home-panel-time">${escapeHtml(entryTime(entry, day))}</span>
            </span>
            <span class="home-panel-title">${escapeHtml(entry.event.title)}</span>
        </a>`).join('');

    paint('home-calendar-body', myRender, rows);
}

async function fillEventsPanel(myRender) {
    // Through the shared cache, so opening Home does not cost a request the sidebar has
    // already made (and the sidebar gets the benefit when Home is opened first).
    const events = await getEvents();
    const now = Date.now();

    // Live first, then the soonest to start. A finished event keeps its page but is not
    // "coming up" -- the Events page is the full list.
    const current = events
        .filter((ev) => isLive(ev, now) || new Date(ev.starts_at).getTime() > now)
        .sort((a, b) => {
            const liveDelta = Number(isLive(b, now)) - Number(isLive(a, now));
            return liveDelta || new Date(a.starts_at) - new Date(b.starts_at);
        })
        .slice(0, EVENT_LIMIT);

    if (current.length === 0) {
        paint('home-events-body', myRender, emptyPanel(
            '/events?new=1', 'fa-plus',
            events.length === 0 ? 'No events yet.' : 'Nothing coming up.', 'Create one',
        ));
        return;
    }

    const rows = current.map((ev) => {
        const starts = new Date(ev.starts_at);
        const live = isLive(ev, now);
        return `
            <a href="${eventHref(ev)}" data-link class="home-panel-row${live ? ' is-live' : ''}">
                <span class="home-panel-when">
                    ${live
                        ? '<span class="event-badge event-badge-live">Live now</span>'
                        : `<span class="home-panel-day">${escapeHtml(dayLabel(startOfDay(starts)))}</span>
                           <span class="home-panel-time">${escapeHtml(starts.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }))}</span>`}
                </span>
                <span class="home-panel-title">${escapeHtml(ev.name)}</span>
            </a>`;
    }).join('');

    paint('home-events-body', myRender, rows);
}
